import { nextTick } from 'vue'
import {
  parseGuestLoginQuery,
  queryWithoutGuestParam,
  type GuestLoginDeepLinkMode
} from '~/utils/guestLoginDeepLink'

export function useGuestLoginDeepLinkPending () {
  return useState<GuestLoginDeepLinkMode | null>('guest-login-deep-link-pending', () => null)
}

function useOpenedGuestDeepLinkUrls () {
  return useState<string[]>('guest-login-deep-link-opened-urls', () => [])
}

/**
 * Opens the guest login modal from `?guest=login|register` (banner deep link),
 * then drops only that param so a refresh does not reopen the modal. UTM and
 * other query keys stay. No-ops when the feature is off or the guest is
 * already signed in.
 *
 * Strip MUST run before open(): UiModal pushState + close() uses history.back().
 * If the banner URL is still the previous history entry, back restores
 * `?guest=register` and the query watch opens the modal again in a loop.
 *
 * The same URL is not opened twice in a session: a restored banner URL after
 * close/back only strips `guest` again.
 */
let inFlight: Promise<void> | null = null

export function applyGuestLoginDeepLink (): Promise<void> {
  if (inFlight) {
    return inFlight
  }
  inFlight = applyGuestLoginDeepLinkOnce().finally(() => {
    inFlight = null
  })
  return inFlight
}

async function applyGuestLoginDeepLinkOnce (): Promise<void> {
  const route = useRoute()
  const mode = parseGuestLoginQuery(route.query.guest)
  if (!mode) {
    return
  }

  const pending = useGuestLoginDeepLinkPending()
  pending.value = mode

  const stripGuestParam = async () => {
    const { query, changed } = queryWithoutGuestParam({ ...route.query })
    if (!changed) {
      return
    }
    await useRouter().replace({ path: route.path, query, hash: route.hash })
  }

  if (!useGuestLoginEnabled()) {
    await stripGuestParam()
    return
  }

  const { getSession, isLoggedIn } = useGuest()
  await getSession()
  if (isLoggedIn.value) {
    await stripGuestParam()
    return
  }

  const openedUrls = useOpenedGuestDeepLinkUrls()
  const urlKey = route.fullPath
  const alreadyOpened = openedUrls.value.includes(urlKey)

  await stripGuestParam()
  await nextTick()

  if (alreadyOpened) {
    return
  }

  openedUrls.value = [...openedUrls.value, urlKey]
  const { open } = useGuestLoginModal()
  open(mode, 'deep_link')
}
